import * as React from 'react';
import { styled } from '@linaria/react';
import { Plus as PlusIcon } from 'lucide-react';

import { BREAKPOINTS } from '@/constants';

import Demo from '@/components/Demo';
import VisuallyHidden from '@/components/VisuallyHidden';
import CodeSnippetProvider from '@/components/CodeSnippet/CodeSnippetProvider';

import CheckmarkButton from './CheckmarkButton';
import { useTrackedOpacity } from './PartialKeyframes.helpers';
import OpacityLabel from './OpacityLabel';
import { constructSnippet } from './RealWorldUseCaseDemo.helpers';

const RESTING_OPACITY = 0.7;

interface Props {
  codeOutput: React.ReactNode;
  initialIsToggled?: boolean;
}

function RealWorldUseCaseDemo({
  codeOutput,
  initialIsToggled = false,
}: Props) {
  const [isToggled, setIsToggled] = React.useState(initialIsToggled);

  const plusButtonRef = React.useRef<HTMLButtonElement>(null);
  const displayedOpacity = useTrackedOpacity(
    RESTING_OPACITY,
    plusButtonRef,
    isToggled
  );

  const codeSnippet = constructSnippet(isToggled);

  return (
    <OuterWrapper>
      <Demo
        initialValues={{}}
        outputWrapper={null}
        controlPosition="top"
        customGrid={{
          gridTemplateColumns: 'min(400px, 100%)',
          justifyContent: 'center',
        }}
      >
        {() => {
          return (
            <Wrapper>
              <Demonstration data-can-be-annotated="false">
                <DemoInnerWrapper>
                  <OpacityLabel opacity={displayedOpacity} />
                  <PlusButtonWrapper>
                    <BtnSocket />
                    {isToggled && (
                      <PlusButton
                        ref={plusButtonRef}
                        style={{
                          '--resting-opacity': RESTING_OPACITY,
                        }}
                      >
                        <Icon aria-hidden="true" size={24} />
                        <VisuallyHidden>
                          Fake button. Just here to demonstrate the
                          opacity changes.
                        </VisuallyHidden>
                      </PlusButton>
                    )}
                  </PlusButtonWrapper>
                </DemoInnerWrapper>
                <MountButton
                  isToggled={isToggled}
                  onClick={() => setIsToggled(!isToggled)}
                >
                  Render element
                </MountButton>
              </Demonstration>
              <SnippetWrapper>
                <CodeSnippetProvider lang="html" code={codeSnippet}>
                  {codeOutput}
                </CodeSnippetProvider>
              </SnippetWrapper>
            </Wrapper>
          );
        }}
      </Demo>
    </OuterWrapper>
  );
}

// On larger windows, we want the text in the code snippet to line up with the body paragraphs:
const OuterWrapper = styled.div`
  @media (min-width: 50rem) {
    margin-inline: -16px;
  }
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: row-reverse;
  flex-wrap: wrap;
  gap: 16px;

  @media ${BREAKPOINTS.smAndSmaller} {
    flex-direction: column;
  }

  @media ${BREAKPOINTS.smAndLarger} {
    margin: -16px;
    /* For some reason, this is required for symmetrical margins: */
    margin-top: -8px;
  }
`;

const SnippetWrapper = styled.div`
  --code-font-size: 0.875rem;
  --code-line-height: 1.325rem;
  flex: 2;
  width: 27rem;
  min-width: 27rem;
  min-height: 28.125rem;
  border: 1px solid var(--color-gray-300);
  border-radius: 8px;
  text-align: left;
  white-space: pre-wrap;
  font-size: 0.875rem;

  @media ${BREAKPOINTS.smAndSmaller} {
    width: 100%;
    min-width: 0;
    min-height: 0;
    max-width: 100%;
    overflow-x: auto;
  }
`;

const Demonstration = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 32px;
  min-width: 17rem;
  padding: 16px;
  border: 1px solid var(--color-gray-300);
  border-radius: 8px;

  @media ${BREAKPOINTS.smAndSmaller} {
    gap: 16px;
    padding-block: 24px;
  }
`;

const DemoInnerWrapper = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

const PlusButtonWrapper = styled.div`
  position: relative;
  height: 4rem;
  width: 4rem;
  background: var(--color-gray-100);
  border-radius: 8px;
`;

const PlusButton = styled.button`
  position: relative;
  width: 4rem;
  height: 4rem;
  display: grid;
  place-content: center;
  background: white;
  border: none;
  border-radius: 8px;
  color: black;
  cursor: pointer;
  opacity: var(--resting-opacity);
  animation: fadeFromTransparent 1000ms;
  -webkit-tap-highlight-color: transparent;
  user-select: none;

  &:hover,
  &:active,
  &:focus-visible {
    opacity: 1;
  }
`;

const Icon = styled(PlusIcon)`
  color: black;
  display: block;

  ${PlusButton}:active & {
    transform: scale(0.8);
  }
`;

const BtnSocket = styled.div`
  position: absolute;
  inset: 0;
  width: 4rem;
  height: 4rem;
  border-radius: 8px;
  border: 2px solid var(--color-gray-300);
  pointer-events: none;
  background: url('/images/partial-keyframes/transparent-white-16x16-10p.png')
    repeat;
`;

const MountButton = styled(CheckmarkButton)`
  width: 11rem;
  height: 3rem;
  padding: 0;
`;

export default RealWorldUseCaseDemo;
